import React, { useEffect, useState, useMemo, useRef, memo, useCallback } from 'react';
import avatars from '../data/avatar-data.json';
import '../utils/avatar-gallery.css';
import { glyphPaths } from '../icons/icon-paths';
import { Canvg } from 'canvg';
import GlyphSvg from '../components/Glyphs';
import { usePageTitle } from '../hooks/usePageTitle';
import { getAvatarTitle } from '../config/page-titles';
import { SimpleResponsivePicture } from '../components/SimpleResponsiveImage';
import ResponsiveAvatarThumbnail from '../components/ResponsiveAvatarThumbnail';
import ResponsiveImage from '../components/ResponsiveImage';
import ResponsiveGalleryImage from '../components/ResponsiveGalleryImage';

function AvatarGallery() {
  const [genderFilter, setGenderFilter] = useState('all');
  const [avatarIndex, setAvatarIndex] = useState(0);
  const [lastScrollTime, setLastScrollTime] = useState(0);
  const [loadedImages, setLoadedImages] = useState([]);
  const [imageIndex, setImageIndex] = useState(0);
  const [isLoadingHeadshot, setIsLoadingHeadshot] = useState(true);
  const [headshotVisible, setHeadshotVisible] = useState(false);
  const [preloadedHeadshots, setPreloadedHeadshots] = useState(new Map());
  const [imageLoadingStates, setImageLoadingStates] = useState(new Map());
  const [glyphGrid, setGlyphGrid] = useState(null);
  const [animationPhase, setAnimationPhase] = useState('idle'); // 'idle', 'toPosition', 'pausing', 'toG11'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAvatarGrid, setShowAvatarGrid] = useState(true);
  const isLoading = isLoadingHeadshot;
  const [selectedAvatarId, setSelectedAvatarId] = useState(null);
  // Removed forceProcess - was causing cascading re-renders
  // Moved to ref to prevent re-renders when images finish preloading
  const preloadedImagesRef = useRef(new Map());
  const [isAvatarTransitioning, setIsAvatarTransitioning] = useState(false);
  
  const processingTimeoutRef = useRef(null);
  const hasPreloadedRef = useRef(false);
  const isProcessingRef = useRef(false);
  const lastProcessedAvatarRef = useRef(null);

  // Hash-based navigation functions
  const updateHash = useCallback((avatarId, gender = null) => {
    if (avatarId) {
      window.location.hash = `#${avatarId}`;
    } else if (gender && gender !== 'all') {
      window.location.hash = `#${gender}`;
    } else {
      window.location.hash = '';
    }
  }, []);

  const getAvatarFromHash = () => {
    const hash = window.location.hash.slice(1); // Remove the #
    if (hash) {
      // Check if it's an avatar ID
      const avatar = avatars.find(avatar => avatar.id === hash);
      if (avatar) return avatar;
      
      // Check if it's a gender filter
      if (['female', 'male'].includes(hash)) {
        return { type: 'filter', gender: hash };
      }
    } else {
      // Empty hash means show all avatars
      return { type: 'filter', gender: 'all' };
    }
    return null;
  };


  const SCROLL_COOLDOWN = 800;
  const CELL_SIZE = 10;
  const GLYPH_RES = 24;
  const GYLPH_COLOR = "#3520D6";
  const ANIMATION_SPEED = 40; // Milliseconds per glyph transition step
  const ANIMATION_OUT_SPEED = 40; // Out Speed
  const CROSSFADE_DURATION = 400; // 0.8 seconds for crossfade in milliseconds
  const GLYPH_PAUSE = 250; // 100ms pause after animation complete

  const canvasRef = useRef(null);
  const glyphStateRef = useRef([]);
  const glyphImgsRef = useRef({});
  const animationRef = useRef(null);
  const targetGridRef = useRef(null);
  const activeImageRef = useRef(null);
  const sidebarRef = useRef(null);
  const [canvasStyle, setCanvasStyle] = useState({});

  const glyphIds = [
    'g11', 'g10', 'g09', 'g08', 'g07', 'g06', 'g05', 'g04', 'g03', 'g02', 'g01'
  ];

  const filteredAvatars = useMemo(() => {
    return avatars.filter((avatar) => {
      if (genderFilter === 'all') return true;
      return avatar.details.gender.toLowerCase() === genderFilter;
    });
  }, [genderFilter]);

  // Set currentAvatar based on selected avatar or current index
  const currentAvatar = useMemo(() => {
    if (selectedAvatarId) {
      return filteredAvatars.find(avatar => avatar.id === selectedAvatarId);
    } else if (!showAvatarGrid) {
      return filteredAvatars[avatarIndex] || null;
    }
    return null;
  }, [selectedAvatarId, filteredAvatars, showAvatarGrid, avatarIndex]);

  // Title management - memoize to prevent excessive re-renders
  const titleConfig = useMemo(() => ({
    title: currentAvatar ? getAvatarTitle(currentAvatar.details.name) : 
           getAvatarTitle(null, genderFilter),
    description: currentAvatar ? 
      `View ${currentAvatar.details.name}'s AI-generated avatar. Professional headshot with customizable styles.` :
      `Browse our collection of ${genderFilter !== 'all' ? genderFilter : 'AI-generated'} avatars. Professional and creative options available.`,
    image: currentAvatar ? `/${currentAvatar.headshot?.[0]}` : undefined,
    url: `${window.location.origin}/avatars${currentAvatar ? `#${currentAvatar.id}` : genderFilter !== 'all' ? `#${genderFilter}` : ''}`
  }), [currentAvatar?.id, genderFilter]);

  usePageTitle(titleConfig);

  const changeImage = useCallback((direction) => {
    if (!loadedImages.length) return;
    setImageIndex((prevIndex) => {
      const len = loadedImages.length;
      const newIndex = (prevIndex + direction + len) % len;
      return newIndex;
    });
  }, [loadedImages.length]);

  const allImages = useMemo(() => {
    if (!currentAvatar) return [];
    const headshot = currentAvatar.headshot?.[0] ? [`/${currentAvatar.headshot[0]}`] : [];
    const shots = (currentAvatar.shots || []).map(s => `/${s}`);
    return [...headshot, ...shots];
  }, [currentAvatar]);

  const cacheGlyphBitmaps = async () => {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = GLYPH_RES;
    tempCanvas.height = GLYPH_RES;
    const ctx = tempCanvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;

    for (const id of glyphIds) {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" fill="${GYLPH_COLOR}" width="24" height="24" viewBox="0 0 24 24">${glyphPaths[id]}</svg>`;
      const v = await Canvg.fromString(ctx, svg);
      await v.render();

      const outCanvas = document.createElement('canvas');
      outCanvas.width = GLYPH_RES;
      outCanvas.height = GLYPH_RES;
      outCanvas.getContext('2d').drawImage(tempCanvas, 0, 0);
      glyphImgsRef.current[id] = outCanvas;
      ctx.clearRect(0, 0, GLYPH_RES, GLYPH_RES);
    }
  };

  useEffect(() => {
    cacheGlyphBitmaps();
  }, []);

  // Sidebar hover effects - now handled by CSS
  // Removed GSAP-based effects in favor of CSS transforms

  // Handle hash-based navigation on initial load and hash changes
  useEffect(() => {
    let isInitialMount = true;
    
    const handleHashChange = () => {
      const hashResult = getAvatarFromHash();
      if (hashResult) {
        if (hashResult.type === 'filter') {
          // Handle gender filter - batch state updates
          setGenderFilter(hashResult.gender);
          setShowAvatarGrid(true);
          setSelectedAvatarId(null);
          setAvatarIndex(0);
        } else {
          // Handle avatar selection - use current avatars list instead of dependency
          const currentFilteredAvatars = avatars.filter((avatar) => {
            if (genderFilter === 'all') return true;
            return avatar.details.gender.toLowerCase() === genderFilter;
          });
          
          const idx = currentFilteredAvatars.findIndex(a => a.id === hashResult.id);
          if (idx !== -1) {
            // Clear any pending processing
            if (processingTimeoutRef.current) {
              clearTimeout(processingTimeoutRef.current);
            }
            
            // Batch state updates
            setAvatarIndex(idx);
            setSelectedAvatarId(hashResult.id);
            setShowAvatarGrid(false);
          }
        }
      }
    };

    // Handle initial hash only if there's actually a hash
    if (window.location.hash) {
      handleHashChange();
    }

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []); // Remove filteredAvatars dependency

  // Remove this duplicate effect - hash handling is already in the previous useEffect
  // useEffect(() => {
  //   const hashResult = getAvatarFromHash();
  //   if (hashResult && hashResult.type === 'filter') {
  //     setGenderFilter(hashResult.gender);
  //     setShowAvatarGrid(true);
  //   }
  // }, []); // Removed duplicate hash handling

  // Animation functions - defined before event handlers
  const animateGlyphsToClear = useCallback(() => {
    if (!glyphStateRef.current || glyphStateRef.current.length === 0) {
      return;
    }

    setAnimationPhase('toClear');

    const grid = glyphStateRef.current;
    const totalCells = grid.length * grid[0].length;

    // Create array of all cell positions
    const allPositions = [];
    for (let y = 0; y < grid.length; y++) {
      for (let x = 0; x < grid[0].length; x++) {
        if (grid[y][x] !== null && grid[y][x] !== undefined) {
          allPositions.push({ x, y });
        }
      }
    }

    // Shuffle positions for random order
    for (let i = allPositions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allPositions[i], allPositions[j]] = [allPositions[j], allPositions[i]];
    }

    const CLEAR_ANIMATION_SPEED = 15; // How often to clear more cells
    const CELLS_PER_STEP = Math.max(1, Math.floor(allPositions.length / 30)); // Clear multiple cells per step
    let currentIndex = 0;

    const animate = () => {
      if (currentIndex >= allPositions.length) {
        // Final clear - ensure everything is empty
        const clearedGrid = grid.map(row => row.map(() => null));
        setGlyphGrid(clearedGrid);
        setAnimationPhase('idle');
        
        // Fade in the headshot now that animation is complete
        console.log('Animation complete - fading in headshot');
        setHeadshotVisible(true);

        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        return;
      }

      // Create new grid based on current state
      const newGrid = glyphStateRef.current.map(row => [...row]);

      // Clear the next batch of cells
      for (let i = 0; i < CELLS_PER_STEP && currentIndex < allPositions.length; i++) {
        const pos = allPositions[currentIndex];
        newGrid[pos.y][pos.x] = null;
        currentIndex++;
      }

      setGlyphGrid(newGrid);
    };

    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    animationRef.current = setInterval(animate, CLEAR_ANIMATION_SPEED);
  }, []);

  // Event handlers - defined outside useEffect to avoid circular dependencies
  const changeAvatar = useCallback((direction) => {
    setIsAvatarTransitioning(true); // Start transition
    setAvatarIndex((prevIndex) => {
      const len = filteredAvatars.length;
      const newIndex = (prevIndex + direction + len) % len;
      const newAvatar = filteredAvatars[newIndex];
      if (newAvatar) {
        setSelectedAvatarId(newAvatar.id); // Keep selectedAvatarId in sync
        updateHash(newAvatar.id);
      }
      return newIndex;
    });
    setLastScrollTime(Date.now());
  }, [filteredAvatars, updateHash]);

  const handleWheel = useCallback((e) => {
    if (showAvatarGrid) return; // Disable when selection grid is active
    if (Date.now() - lastScrollTime < SCROLL_COOLDOWN) return;
    if (e.deltaY > 0) changeAvatar(1);
    else if (e.deltaY < 0) changeAvatar(-1);
  }, [showAvatarGrid, lastScrollTime, changeAvatar]);

  const handleKey = useCallback((e) => {
    if (showAvatarGrid) return; // Disable when selection grid is active
    if (Date.now() - lastScrollTime < SCROLL_COOLDOWN) return;
    
    // Handle fullscreen mode
    if (isFullscreen && e.key === 'Escape') {
      setIsFullscreen(false);
      return;
    }
    
    if (e.key === 'ArrowDown') changeAvatar(1);
    else if (e.key === 'ArrowUp') changeAvatar(-1);
    else if (e.key === 'ArrowLeft') changeImage(-1);
    else if (e.key === 'ArrowRight') changeImage(1);
    else if (e.key === 'c' || e.key === 'C') animateGlyphsToClear();
  }, [showAvatarGrid, lastScrollTime, changeAvatar, changeImage, animateGlyphsToClear, isFullscreen]);

  const handleTouchStart = useCallback((e) => {
    if (showAvatarGrid) return; // Disable when selection grid is active
    window.touchStartY = e.touches?.[0]?.clientY || null;
    window.touchStartX = e.touches?.[0]?.clientX || null;
  }, [showAvatarGrid]);

  const handleTouchEnd = useCallback((e) => {
    if (showAvatarGrid) return; // Disable when selection grid is active
    if (window.touchStartY === null && window.touchStartX === null) return;

    const touchEndX = e.changedTouches?.[0]?.clientX;
    const touchEndY = e.changedTouches?.[0]?.clientY;
    const deltaX = window.touchStartX - touchEndX;
    const deltaY = window.touchStartY - touchEndY;

    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      if (Math.abs(deltaY) > 30 && Date.now() - lastScrollTime > SCROLL_COOLDOWN) {
        changeAvatar(deltaY > 0 ? 1 : -1);
      }
    } else if (Math.abs(deltaX) > 50 && Date.now() - lastScrollTime > SCROLL_COOLDOWN) {
      changeImage(deltaX > 0 ? 1 : -1);
    }

    window.touchStartY = null;
    window.touchStartX = null;
  }, [showAvatarGrid, lastScrollTime, changeAvatar, changeImage]);

  // Main event listener effect
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    // Set up event listeners
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('keydown', handleKey);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      document.body.style.overflow = '';
      
      // Clear any timeouts or intervals that might be running
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    };
  }, [handleWheel, handleKey, handleTouchStart, handleTouchEnd]); // Use stable callbacks

  // When the filter or avatar list changes, sync avatarIndex to selectedAvatarId
  useEffect(() => {
    if (!selectedAvatarId) return;
    const currentFilteredAvatars = avatars.filter((avatar) => {
      if (genderFilter === 'all') return true;
      return avatar.details.gender.toLowerCase() === genderFilter;
    });
    const idx = currentFilteredAvatars.findIndex(a => a.id === selectedAvatarId);
    if (idx !== -1) {
      setAvatarIndex(idx);
      // Removed forceProcess - processing will happen via currentAvatar change
    }
  }, [selectedAvatarId, genderFilter]); // Remove filteredAvatars dependency

  // ResizeObserver for canvas sizing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    const resize = () => {
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
    };
    resize();

    const observer = new window.ResizeObserver(resize);
    observer.observe(parent);
    window.addEventListener('resize', resize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [showAvatarGrid]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Clean up all global event listeners and state
      document.body.style.overflow = '';
      
      // Clear any remaining timeouts or intervals
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
      
      // Reset any global state
      setAnimationPhase('idle');
      setIsAvatarTransitioning(false);
      setIsLoadingHeadshot(false);
    };
  }, []);

  // Update canvas size and position to match the active image
  useEffect(() => {
    if (!canvasRef.current) return;
    
    // If we have an active image, use its dimensions
    if (activeImageRef.current) {
      const img = activeImageRef.current;
      const canvas = canvasRef.current;
      const width = img.width || img.offsetWidth;
      const height = img.height || img.offsetHeight;
      
      if (width > 0 && height > 0) {
        canvas.width = width;
        canvas.height = height;
        setCanvasStyle({
          position: 'absolute',
          top: 0,
          left: 0,
          width,
          height,
          pointerEvents: 'none',
          zIndex: 5
        });
        return;
      }
    }
    
    // Fallback: use parent container dimensions for initial sizing
    const canvas = canvasRef.current;
    const parent = canvas.parentElement;
    if (parent) {
      const width = parent.offsetWidth;
      const height = parent.offsetHeight;
      canvas.width = width;
      canvas.height = height;
      setCanvasStyle({
        position: 'absolute',
        top: 0,
        left: 0,
        width,
        height,
        pointerEvents: 'none',
        zIndex: 5
      });
    }
  }, [imageIndex, loadedImages, showAvatarGrid, isFullscreen, headshotVisible]);

  // Update canvas size to match headshot image after it's loaded
  useEffect(() => {
    if (!headshotVisible || !canvasRef.current) return;
    
    const updateCanvasSize = () => {
      const canvas = canvasRef.current;
      const headshotImg = document.querySelector('.avatar-headshot img');
      
      if (headshotImg && canvas) {
        const displayedWidth = headshotImg.offsetWidth;
        const displayedHeight = headshotImg.offsetHeight;
        
        if (displayedWidth > 0 && displayedHeight > 0) {
          // Since headshot uses object-fit: cover, the image fills the entire container
          // So canvas should match the container dimensions exactly
          canvas.width = displayedWidth;
          canvas.height = displayedHeight;
          setCanvasStyle({
            position: 'absolute',
            top: 0,
            left: 0,
            width: displayedWidth,
            height: displayedHeight,
            pointerEvents: 'none',
            zIndex: 5
          });
        }
      }
    };
    
    // Update immediately
    updateCanvasSize();
    
    // Also update after a short delay to ensure image is fully rendered
    const timeoutId = setTimeout(updateCanvasSize, 100);
    
    return () => clearTimeout(timeoutId);
  }, [headshotVisible, currentAvatar]);


  const drawToBufferAndBlit = (grid) => {
    console.log('=== drawToBufferAndBlit called ===');
    console.log('Grid:', grid);
    console.log('Canvas ref:', canvasRef.current);
    
    if (!grid || !canvasRef.current) {
      console.log('No grid or canvas, returning');
      return;
    }
    const mainCanvas = canvasRef.current;
    const width = mainCanvas.width;
    const height = mainCanvas.height;

    console.log('Canvas dimensions:', width, 'x', height);

    if (width === 0 || height === 0) {
      console.log('Canvas has 0 dimensions, returning');
      return; // Prevent drawing on a 0x0 canvas
    }

    const buffer = document.createElement('canvas');
    buffer.width = width;
    buffer.height = height;
    const bctx = buffer.getContext('2d');

    for (let y = 0; y < grid.length; y++) {
      for (let x = 0; x < grid[0].length; x++) {
        const glyphIndex = grid[y][x];

        // Skip drawing if the glyph is empty (null or undefined)
        if (glyphIndex === null || glyphIndex === undefined) {
          continue;
        }

        const glyph = glyphImgsRef.current[glyphIds[glyphIndex]];
        if (glyph && glyph.width > 0 && glyph.height > 0) {
          bctx.drawImage(glyph, x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        }
      }
    }

    const ctx = mainCanvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(buffer, 0, 0);

    glyphStateRef.current = grid;
  };

  const animateGlyphsToPosition = useCallback(() => {
    console.log('=== animateGlyphsToPosition START ===');
    console.log('animateGlyphsToPosition called, targetGridRef.current:', targetGridRef.current);
    if (!targetGridRef.current) {
      console.log('No target grid, returning');
      return;
    }

    console.log('Starting animation to position');
    console.log('Animation phase set to: toPosition');
    setAnimationPhase('toPosition');
    const maxSteps = glyphIds.length - 1;
    let currentStep = 0;
    console.log('Max steps:', maxSteps);

    const animate = () => {
      console.log(`=== ANIMATION RUNNING: step ${currentStep}/${maxSteps} ===`);
      console.log('ANIMATE FUNCTION IS BEING CALLED!');
      if (currentStep >= maxSteps || !targetGridRef.current) {
        console.log('Animation complete, setting final grid');
        setGlyphGrid(targetGridRef.current);
        setAnimationPhase('pausing');

        // Pause for 100ms then animate to G11
        setTimeout(() => {
          console.log('Starting animateGlyphsToG11');
          animateGlyphsToG11();
        }, GLYPH_PAUSE);

        if (animationRef.current) {
          console.log('Clearing animation interval in animateGlyphsToPosition completion:', animationRef.current);
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        return;
      }

      const newGrid = targetGridRef.current.map((row, y) =>
        row.map((targetIndex, x) => {
          const currentIndex = glyphStateRef.current[y]?.[x] || 0;
          return Math.min(currentIndex + 1, targetIndex);
        })
      );

      setGlyphGrid(newGrid);
      currentStep++;
    };

    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    console.log('Setting up animation interval with speed:', ANIMATION_SPEED);
    animationRef.current = setInterval(animate, ANIMATION_SPEED);
    console.log('Animation interval set:', animationRef.current);
  }, []);

  const animateGlyphsToG11 = useCallback(() => {
    console.log('=== animateGlyphsToG11 START ===');
    if (!targetGridRef.current) {
      console.log('No target grid in animateGlyphsToG11, returning');
      return;
    }

    console.log('Setting animation phase to toG11');
    setAnimationPhase('toG11');

    // Start the crossfade when the animation begins
    console.log('Setting headshotVisible to true');
    setHeadshotVisible(true);

    const maxSteps = glyphIds.length - 1;
    const stepsNeeded = Math.max(1, Math.ceil(CROSSFADE_DURATION / ANIMATION_OUT_SPEED));
    let currentStep = 0;

    const animate = () => {
      if (currentStep >= stepsNeeded || !targetGridRef.current) {
        const g11Grid = targetGridRef.current.map(row => row.map(() => 0));
        setGlyphGrid(g11Grid);
        setAnimationPhase('idle');
        setIsAvatarTransitioning(false); // End transition
        // Remove this line since we're now setting headshotVisible at the start
        // setHeadshotVisible(true);

        // Trigger clear animation after crossfade completes
        setTimeout(() => {
          console.log('Starting animateGlyphsToClear');
          animateGlyphsToClear();
        }, CROSSFADE_DURATION);

        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        return;
      }

      const progress = (currentStep + 1) / stepsNeeded;
      const newGrid = targetGridRef.current.map((row, y) =>
        row.map((currentIndex) => {
          const targetIndex = 0; // g11
          const stepsToTarget = currentIndex - targetIndex;
          const stepsToMove = Math.ceil(stepsToTarget * progress);
          return Math.max(currentIndex - stepsToMove, 0);
        })
      );

      setGlyphGrid(newGrid);
      currentStep++;
    };

    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    console.log('Setting up animation interval with speed:', ANIMATION_SPEED);
    animationRef.current = setInterval(animate, ANIMATION_SPEED);
    console.log('Animation interval set:', animationRef.current);
  }, []);

  const processAvatarImage = () => {
    console.log('=== processAvatarImage START ===');
    console.log('processAvatarImage called for:', currentAvatar?.id);
    console.log('Canvas ref:', canvasRef.current);
    console.log('Canvas dimensions:', canvasRef.current?.width, 'x', canvasRef.current?.height);
    
    // Use headshot first, then first shot, then return if none available
    const imageToProcess = currentAvatar?.headshot?.[0];
    if (!imageToProcess) {
      console.log('No image to process');
      return;
    }
    
    // Convert to WebP path for processing
    const relativePath = imageToProcess.replace(/^\/img\//, '');
    const pathParts = relativePath.split('/');
    const filename = pathParts[pathParts.length - 1];
    const filenameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    const directoryPath = pathParts.slice(1, -1).join('/');
    const webpPath = `/img/cache/${directoryPath}/${filenameWithoutExt}-large-1440w.webp`;
    
    const canvas = canvasRef.current;
    if (!canvas) {
      console.log('No canvas available');
      return;
    }
    
    console.log('Processing image:', imageToProcess);
    console.log('Using WebP path for canvas processing:', webpPath);
    const ctx = canvas?.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    // Load WebP image for processing
    img.src = webpPath;

    const tryProcess = () => {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      
      if (canvasWidth === 0 || canvasHeight === 0 || img.width === 0 || img.height === 0) {
        // Retry after a short delay
        setTimeout(tryProcess, 30);
        return;
      }

      const imgAspect = img.width / img.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth, drawHeight, offsetX, offsetY;
      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = img.width * (canvasHeight / img.height);
        offsetX = (canvasWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = img.height * (canvasWidth / img.width);
        offsetX = 0;
        offsetY = (canvasHeight - drawHeight) / 2;
      }

      const offscreen = document.createElement('canvas');
      offscreen.width = canvasWidth;
      offscreen.height = canvasHeight;
      const offCtx = offscreen.getContext('2d');
      offCtx.imageSmoothingEnabled = false;
      offCtx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      const imageData = offCtx.getImageData(0, 0, canvasWidth, canvasHeight).data;
      const targetGrid = [];
      for (let y = 0; y < canvasHeight; y += CELL_SIZE) {
        const row = [];
        for (let x = 0; x < canvasWidth; x += CELL_SIZE) {
          const i = (y * canvasWidth + x) * 4;
          const brightness = (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
          const index = glyphIds.length - 1 - Math.floor((brightness / 255) * (glyphIds.length - 1));
          row.push(index);
        }
        targetGrid.push(row);
      }

      // Store target grid for animations
      targetGridRef.current = targetGrid;

      // Initialize grid with all g11 (index 0)
      const initialGrid = targetGrid.map(row => row.map(() => 0));
      setGlyphGrid(initialGrid);

      // Mark this avatar as processed
      lastProcessedAvatarRef.current = currentAvatar.id;

      // Start animation to position
      console.log('=== STARTING ANIMATION ===');
      console.log('Target grid:', targetGrid);
      console.log('Starting animateGlyphsToPosition');
      animateGlyphsToPosition();
    };

    img.onload = () => {
      console.log('=== IMAGE LOADED ===');
      console.log('Image dimensions:', img.width, 'x', img.height);
      console.log('Canvas dimensions:', canvas.width, 'x', canvas.height);
      console.log('Starting tryProcess');
      tryProcess();
    };
    img.onerror = () => {
      console.log('=== IMAGE FAILED TO LOAD ===');
      setGlyphGrid(null);
      targetGridRef.current = null;
    };
  };

  // Preload all avatar images for instant loading
  const preloadAvatarImages = () => {
    const imageMap = new Map();
    const loadingPromises = [];
    
    avatars.forEach(avatar => {
      const avatarImages = [];
      
      // Add headshot images with WebP optimization
      if (avatar.headshot && avatar.headshot.length > 0) {
        avatar.headshot.forEach(headshot => {
          // Use WebP optimized image
          const relativePath = headshot.replace(/^\/img\//, '');
          const pathParts = relativePath.split('/');
          const filename = pathParts[pathParts.length - 1];
          const filenameWithoutExt = filename.replace(/\.[^/.]+$/, '');
          const directoryPath = pathParts.slice(1, -1).join('/');
          const webpPath = `/img/cache/${directoryPath}/${filenameWithoutExt}-large-1440w.webp`;
          avatarImages.push(webpPath);
        });
      }
      
      // Add shot images with WebP optimization
      if (avatar.shots && avatar.shots.length > 0) {
        avatar.shots.forEach(shot => {
          // Use WebP optimized image
          const relativePath = shot.replace(/^\/img\//, '');
          const pathParts = relativePath.split('/');
          const filename = pathParts[pathParts.length - 1];
          const filenameWithoutExt = filename.replace(/\.[^/.]+$/, '');
          const directoryPath = pathParts.slice(1, -1).join('/');
          const webpPath = `/img/cache/${directoryPath}/${filenameWithoutExt}-large-1440w.webp`;
          avatarImages.push(webpPath);
        });
      }
      
      // Create loading promises for each image
      avatarImages.forEach(src => {
        const loadPromise = new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve({ src, img, avatarId: avatar.id });
          img.onerror = () => {
            console.warn('Failed to preload image:', src);
            reject(new Error(`Failed to load ${src}`));
          };
          img.src = src;
        });
        
        loadingPromises.push(loadPromise);
      });
    });
    
    // Wait for all images to load and organize by avatar ID
    Promise.allSettled(loadingPromises).then(results => {
      const successfulLoads = results
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);
      
      console.log(`Preloading completed: ${successfulLoads.length}/${loadingPromises.length} images loaded successfully`);
      
      // Organize loaded images by avatar ID
      successfulLoads.forEach(({ src, img, avatarId }) => {
        if (!imageMap.has(avatarId)) {
          imageMap.set(avatarId, { images: [], sources: [] });
        }
        imageMap.get(avatarId).images.push(img);
        imageMap.get(avatarId).sources.push(src);
      });
      
      preloadedImagesRef.current = imageMap;
    }).catch(error => {
      console.error('Preloading failed:', error);
      // Continue with empty preload map - progressive loading will handle it
      preloadedImagesRef.current = new Map();
    });
  };


  // Component unmount cleanup - ensure everything is reset
  useEffect(() => {
    return () => {
      // Reset document body overflow
      document.body.style.overflow = '';
      
      // Clear all timeouts and intervals
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
      
      // Reset component states to prevent interference
      setAnimationPhase('idle');
      setIsAvatarTransitioning(false);
      setIsLoadingHeadshot(true);
      preloadedImagesRef.current = new Map();
      setLoadedImages([]);
      
      // Clear the hash to prevent navigation interference
      if (window.location.hash) {
        window.location.hash = '';
      }
    };
  }, []);



  // Load images - headshot first, then shots
  const loadImages = useCallback(() => {
    if (!currentAvatar) return;

    console.log('Loading images for avatar:', currentAvatar.id);
    
    // Capture the current avatar ID to prevent race conditions
    const currentAvatarId = currentAvatar.id;

    // Create a clean array of image URLs for this avatar
    const avatarImages = [];
    
    // Add headshot first with WebP optimization (prioritize this)
    if (currentAvatar.headshot?.[0]) {
      const headshot = currentAvatar.headshot[0];
      const relativePath = headshot.replace(/^\/img\//, '');
      const pathParts = relativePath.split('/');
      const filename = pathParts[pathParts.length - 1];
      const filenameWithoutExt = filename.replace(/\.[^/.]+$/, '');
      const directoryPath = pathParts.slice(1, -1).join('/');
      const webpPath = `/img/cache/${directoryPath}/${filenameWithoutExt}-large-1440w.webp`;
      avatarImages.push(webpPath);
    }
    
    // Add all shots with WebP optimization
    if (currentAvatar.shots && currentAvatar.shots.length > 0) {
      currentAvatar.shots.forEach(shot => {
        const relativePath = shot.replace(/^\/img\//, '');
        const pathParts = relativePath.split('/');
        const filename = pathParts[pathParts.length - 1];
        const filenameWithoutExt = filename.replace(/\.[^/.]+$/, '');
        const directoryPath = pathParts.slice(1, -1).join('/');
        const webpPath = `/img/cache/${directoryPath}/${filenameWithoutExt}-large-1440w.webp`;
        avatarImages.push(webpPath);
      });
    }
    
    console.log('Loading avatar images (WebP):', avatarImages);
    
    // Set the images array immediately to prevent race conditions
    setLoadedImages(avatarImages);
    setIsLoadingHeadshot(false);
    setHeadshotVisible(false); // Don't show headshot until canvas animation completes
  }, [currentAvatar]);

  useEffect(() => {
    if (glyphGrid) {
      drawToBufferAndBlit(glyphGrid);
    }
  }, [glyphGrid]);





  // Process avatar image when canvas becomes available





  useEffect(() => {
    if (!currentAvatar) return;
    
    console.log('Avatar changed to:', currentAvatar.id);
    
    // Reset states
    setImageIndex(0);
    setLoadedImages([]);
    setIsLoadingHeadshot(true);
    setHeadshotVisible(false);
    setGlyphGrid(null);
    setAnimationPhase('idle');
    targetGridRef.current = null;

    if (animationRef.current) {
      console.log('Clearing animation interval in main useEffect:', animationRef.current);
      clearInterval(animationRef.current);
      animationRef.current = null;
    }

    // Load images first
    loadImages();
    
    // Start processing after a delay to ensure canvas is ready
    setTimeout(() => {
      console.log('Timeout callback - canvas ref:', canvasRef.current);
      if (canvasRef.current) {
        console.log('Calling processAvatarImage from timeout');
        processAvatarImage();
      } else {
        console.log('No canvas ref in timeout');
      }
    }, 100);

    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [currentAvatar?.id]);


  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showAvatarGrid && (e.key === 'Escape' || e.key === 'Backspace')) {
        setShowAvatarGrid(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showAvatarGrid]);

  return (
    <div className="avatar-gallery-fixed">
      {/* FILTER BAR */}
      <div className="avatar-filter-bar">
        {['all', 'female', 'male'].map((gender) => (
          <button
            key={gender}
            className={genderFilter === gender && showAvatarGrid ? 'active' : ''}
            onClick={() => {
              setGenderFilter(gender);
              setShowAvatarGrid(true);
              updateHash(null, gender);
            }}
          >
            {gender[0].toUpperCase() + gender.slice(1)}
          </button>
        ))}
      </div>

      {/* AVATAR SELECTION OVERLAY */}
      {showAvatarGrid && (
        <div className="avatar-selection-overlay">
          <div className="avatar-thumbnail-bar avatar-selection-grid">
            {filteredAvatars.map((avatar, i) => (
              <div
                key={avatar.id}
                className="avatar-thumb-item"
                onClick={() => {
                  console.log('Gallery avatar clicked:', avatar.id);
                  const idx = filteredAvatars.findIndex(a => a.id === avatar.id);
                  if (idx !== -1) {
                    setShowAvatarGrid(false);
                    setAvatarIndex(idx);
                    setSelectedAvatarId(avatar.id);
                    setIsAvatarTransitioning(true);
                    updateHash(avatar.id);
                  }
                }}
              >
                <ResponsiveAvatarThumbnail
                  avatarId={avatar.id}
                  alt={avatar.details?.name || 'Avatar'}
                />
                <span>avatar_{avatar.details?.name?.toLowerCase() || 'unknown'}</span>
                {/* Random glyph in top right */}
                <svg
                  width={12}
                  height={12}
                  viewBox="0 0 24 24"
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    zIndex: 100,
                    display: 'block',
                    pointerEvents: 'none',
                  }}
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d={glyphPaths[`g0${(i % 6) + 1}`]?.match(/d="([^"]+)"/)?.[1] || ''} fill="var(--c0)" />
                </svg>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* GALLERY (always rendered, just hidden when overlay is up) */}
      <div className={showAvatarGrid ? 'hidden' : ''}>
        {loadedImages.length > 0 && !isLoadingHeadshot && (
                      <div
              key={currentAvatar?.id} // Force re-render when avatar changes
              className={`avatar-image-wrapper${isFullscreen ? ' fullscreen' : ''}`}
              onClick={() => setIsFullscreen(f => !f)}
              style={{ cursor: 'pointer' }}
            >
            {isFullscreen ? (
              /* Fullscreen mode - simple single image display */
              <div className="fullscreen-gallery">
                {loadedImages.length > 0 && (
                  <ResponsiveGalleryImage
                    src={loadedImages[imageIndex]}
                    alt={`Avatar view ${imageIndex}`}
                    type={imageIndex === 0 ? 'headshot' : 'shot'}
                    className="fullscreen-image"
                    style={{
                      width: '100vw',
                      height: '100vh',
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                  />
                )}
              </div>
            ) : (
              /* Regular gallery mode */
              <div
                key={`slider-${currentAvatar?.id}`}
                className="avatar-image-slider"
                style={{ 
                  transform: `translateX(-${imageIndex * 100}%)`,
                  display: 'flex',
                  width: `${loadedImages.length * 100}%`,
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out'
                }}
              >
                {/* Render loaded images */}
                {loadedImages.length > 0 ? (
                  loadedImages.map((src, i) => {
                    const isHeadshot = i === 0;
                    const imageType = isHeadshot ? 'headshot' : 'shot';
                    
                    return (
                      <React.Fragment key={`${currentAvatar?.id}-${i}-${src}`}>
                        <ResponsiveGalleryImage
                          src={src}
                          alt={`Avatar view ${i}`}
                          type={imageType}
                          className={`avatar-slide-img${i === imageIndex ? ' active' : ''}`}
                          style={{
                            width: `${100 / loadedImages.length}%`,
                            height: '100%',
                            objectFit: 'cover',
                            flexShrink: 0,
                            opacity: isHeadshot ? (headshotVisible ? 1 : 0) : 1,
                            transition: isHeadshot ? 'opacity 0.5s ease-in' : 'none'
                          }}
                          ref={i === imageIndex ? activeImageRef : undefined}
                        />
                      </React.Fragment>
                    );
                  })
                ) : (
                  <div style={{ 
                    width: '100%', 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: '#f0f0f0',
                    color: '#666'
                  }}>
                    No images loaded
                  </div>
                )}
                
                {/* Canvas overlay for headshot animation - always rendered */}
                <canvas
                  ref={canvasRef}
                  className="avatar-headshot-canvas"
                  style={canvasStyle}
                />
              </div>
            )}

          </div>
        )}

        {isLoading ? (
          <div
            className="image-nav-dots is-loading"
            style={{ '--dot-count': 0 }}
          >
            <span className='loader'>LOADING...</span>
          </div>
        ) : (
          loadedImages.length > 1 && (
            <div
              className="image-nav-dots"
              style={{ '--dot-count': loadedImages.length }}
            >
              {loadedImages.map((_, i) => (
                <span
                  key={i}
                  className={`image-dot ${i === imageIndex ? 'active' : ''}`}
                  onClick={() => setImageIndex(i)}
                />
              ))}
            </div>
          )
        )}

        <div className="avatar-thumbnail-bar" ref={sidebarRef}>
          {filteredAvatars.map((avatar, i) => (
            <div
              key={avatar.id}
              className={`avatar-thumb-item ${i === avatarIndex ? 'active' : ''}`}
              onClick={() => {
                console.log('Sidebar avatar clicked:', avatar.id);
                setAvatarIndex(i);
                setSelectedAvatarId(avatar.id);
                setIsAvatarTransitioning(true);
                updateHash(avatar.id);
              }}
            >
              <span className="avatar-thumb-label">{avatar.details?.name || 'Unknown'}</span>
                  <ResponsiveAvatarThumbnail
                    avatarId={avatar.id}
                    alt={avatar.details?.name || 'Avatar'}
                  />
            </div>
          ))}
        </div>

        <div className="avatar-details-block">
          {currentAvatar && (
            <>
              {currentAvatar.details?.name || 'Unknown'} {currentAvatar.version || 'v1'}
              <GlyphSvg size="10" glyph="g08" />
              {currentAvatar.details?.gender || 'Unknown'}
              <GlyphSvg size="10" glyph="g09" />
              age: {currentAvatar.details?.age || 'Unknown'}
            </>
          )}
        </div>
      </div>

      {/* Likeness overlay - only show on "all" filter */}
      {genderFilter === 'all' && showAvatarGrid && (
        <div className="likeness-overlay">
          <div className="likeness-content">
            <h3 className="likeness-headline">GET PAID FOR YOUR LIKENESS.</h3>
            <p className="likeness-copy">Apply to become an avatar and you can start earning royalty payments for your likeness.</p>
            <button className="likeness-button">Apply Now</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AvatarGallery;